import React from 'react';
import { ShieldAlert, ShieldCheck, Scale } from 'lucide-react';
import '../styles/enterprise.css';
import '../styles/responsive/pages/Solvency.css';
import { useTheme } from '../context/ThemeContext';
import PageHeaderPro from '../components/common/PageHeaderPro';
import KpiStrip from '../components/common/KpiStrip';
import KpiCard from '../components/common/KpiCard';
import StatusBadge from '../components/common/StatusBadge';
import { TableSkeleton } from '../components/common/Skeleton';
import { useGetSolvencyQuery } from '../store/api/adminOpsApi';
import { formatUSD, formatCount } from '../utils/formatAmount';

const Solvency = () => {
  const { mode } = useTheme();
  const { data, isLoading, isFetching, refetch } = useGetSolvencyQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const chains = data?.chains || [];
  const totals = data?.totals || {};
  const breach = data?.breach === true;
  // A per-chain row is in breach when liabilities exceed on-chain holdings.
  const isChainBreach = (c) => Number(c.liabilities || 0) > Number(c.holdings || 0);

  return (
    <div className="ent ent-page solvency-page">
      <PageHeaderPro
        title="Solvency & Liability"
        mode={mode === 'test' ? 'test' : 'live'}
        updatedText={isFetching ? 'Updating…' : 'Updated just now'}
        actions={
          <button className="ent-btn ent-btn-secondary" onClick={() => refetch()}>
            Refresh
          </button>
        }
      />

      {!isLoading && (
        breach ? (
          <div className="solvency-banner breach">
            <ShieldAlert size={18} />
            <div>
              <strong>Solvency breach (SOLV-1)</strong>
              <span>
                {' '}Aggregate merchant liabilities exceed on-chain holdings. Payouts are
                platform-blocked until holdings are reconciled.
              </span>
            </div>
          </div>
        ) : (
          <div className="solvency-banner ok">
            <ShieldCheck size={18} />
            <span>Solvent — on-chain holdings cover all merchant liabilities.</span>
          </div>
        )
      )}

      <KpiStrip>
        <KpiCard label="TOTAL LIABILITIES (USD)" value={`$${formatUSD(totals.liabilitiesUsd || 0)}`} sub="Merchant available balances" />
        <KpiCard label="ON-CHAIN HOLDINGS (USD)" value={`$${formatUSD(totals.holdingsUsd || 0)}`} />
        <KpiCard
          label="SURPLUS / DEFICIT (USD)"
          value={`$${formatUSD(totals.surplusUsd || 0)}`}
          delta={{
            value: totals.surplusUsd >= 0 ? 'Surplus' : 'Deficit',
            direction: totals.surplusUsd >= 0 ? 'up' : 'down',
          }}
        />
        <KpiCard label="CHAINS IN BREACH" value={formatCount(chains.filter(isChainBreach).length)} />
      </KpiStrip>

      <div className="pro-table-wrap">
        {isLoading ? (
          <TableSkeleton rows={6} cols={5} />
        ) : chains.length === 0 ? (
          <div className="empty-state">
            <Scale size={48} />
            <p>No solvency data</p>
            <span>Per-chain liabilities vs holdings will appear here</span>
          </div>
        ) : (
          <table className="pro-table">
            <thead>
              <tr>
                <th>CHAIN</th>
                <th className="amount-cell">LIABILITIES (USD)</th>
                <th className="amount-cell">HOLDINGS (USD)</th>
                <th className="amount-cell">DELTA (USD)</th>
                <th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {chains.map((c) => {
                const delta = Number(c.holdings || 0) - Number(c.liabilities || 0);
                const chainBreach = isChainBreach(c);
                return (
                  <tr key={c.networkId || c.chain}>
                    <td data-label="CHAIN">
                      <span className="cell-2__main">{c.chain || c.name}</span>
                    </td>
                    <td className="amount-cell" data-label="LIABILITIES">
                      <span className="amount-cell__main mono">${formatUSD(c.liabilitiesUsd ?? c.liabilities ?? 0)}</span>
                    </td>
                    <td className="amount-cell" data-label="HOLDINGS">
                      <span className="amount-cell__main mono">${formatUSD(c.holdingsUsd ?? c.holdings ?? 0)}</span>
                    </td>
                    <td className="amount-cell" data-label="DELTA">
                      <span className={`amount-cell__main mono ${chainBreach ? 'deficit' : ''}`}>
                        ${formatUSD(c.deltaUsd ?? delta)}
                      </span>
                    </td>
                    <td data-label="STATUS">
                      <StatusBadge
                        status={chainBreach ? 'failed' : 'active'}
                        label={chainBreach ? 'Breach' : 'Solvent'}
                        dot
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Solvency;
